import React, { useContext, useState, useEffect } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import AnDateField from '../../../../components/DateTime/DateField'
import {
  Alert,
  AlertTitle,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Switch,
} from '@mui/material'
import API from '../../../../services/API'
import BigModal from '../../../../components/Modals/BigModal'
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer'
import EpcrPDF from '../PDF/PatientRecordPDF'
import { TenantSettingsContext } from '../../../../App'
import moment from 'moment'

const AccessForm = () => {
  const [recaptchaToken, setRecaptchaToken] = useState(null)
  const [accessCodeError, setAccessCodeError] = useState(false)
  const [failedAttempts, setFailedAttempts] = useState(0)
  const recaptcha = useGoogleReCaptcha()
  const [executeReady, setExecuteReady] = useState(false)
  const [executeRecaptcha, setExecuteRecaptcha] = useState(null)

  useEffect(() => {
    if (recaptcha?.executeRecaptcha) {
      setExecuteRecaptcha(() => recaptcha.executeRecaptcha)
      setExecuteReady(true)
    }
  }, [recaptcha])
  const [accessCode, setAccessCode] = useState('')
  const [patientSurname, setPatientSurname] = useState('')
  const [patientSurnameUnknown, setPatientSurnameUnknown] = useState(false)
  const [dateOfBirth, setDateOfBirth] = useState(null)
  const [dateOfBirthUnknown, setDateOfBirthUnknown] = useState(false)
  const [accessedBy, setAccessedBy] = useState('')
  const [accessedByOrg, setAccessedByOrg] = useState('')
  const [epcrData, setEPCRData] = useState(null)
  const [invalidError, setInvalidError] = useState(false)
  const [failedRecaptcha, setFailedRecaptcha] = useState(false)
  const [isDraft, setIsDraft] = useState(false)

  const { orgSettings } = useContext(TenantSettingsContext).orgConfig

  const handleSubmit = async (e) => {
    e.preventDefault()

    setFailedRecaptcha(false)
    setIsDraft(false)
    setInvalidError(false)
    if (!executeReady || !executeRecaptcha) {
      console.log('Execute recaptcha not yet available')
      return
    }

    const accessCodePattern = /^[a-zA-Z0-9]{8}$/

    if (!accessCodePattern.test(accessCode)) {
      setAccessCodeError(true)
      return
    }

    const token = await executeRecaptcha('access_form')
    setRecaptchaToken(token)

    const formData = {
      accessCode,
      patientSurname,
      patientSurnameUnknown,
      dateOfBirth,
      dateOfBirthUnknown,
      accessedBy,
      accessedByOrg,
      recaptchaToken: token,
    }

    API.post('/epcr/access-patient-record', formData)
      .then((res) => {
        if (res.status === 200) {
          setEPCRData(res.data)
        } else if (res.status === 403 && res.data === 'failedRecaptcha') {
          setFailedRecaptcha(true)
        } else if (res.status === 403 && res.data === 'draft') {
          setIsDraft(true)
        } else {
          setInvalidError(true)
        }
      })
      .catch(() => setInvalidError(true))
    // Handle incorrect details
    // .catch(() => setFormError(true))
  }

  return (
    <Grid item xs={12}>
      <Stack component="form" onSubmit={handleSubmit}>
        <TextField
          label="Access Code"
          fullWidth
          margin="dense"
          required
          value={accessCode}
          onChange={(e) => setAccessCode(e.target.value)}
          error={accessCodeError}
          helperText={`This code is case sensitive and expires within 24 hours. ${
            accessCodeError ? 'Incorrect Code Format.' : ''
          }`}
          autoComplete="off"
        />
        <TextField
          label="Patient's Surname"
          value={patientSurname}
          onChange={(e) =>
            !patientSurnameUnknown && setPatientSurname(e.target.value)
          }
          fullWidth
          margin="dense"
          required={!patientSurnameUnknown}
          autoComplete="off"
          InputProps={{
            endAdornment: (
              <FormControlLabel
                control={
                  <Switch
                    checked={patientSurnameUnknown}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setPatientSurname('')
                      }
                      setPatientSurnameUnknown(e.target.checked)
                    }}
                  />
                }
                label="Unknown"
              />
            ),
          }}
        />
        <AnDateField
          label="Patient's Date of Birth"
          value={dateOfBirth}
          onChange={(newDate) => !dateOfBirthUnknown && setDateOfBirth(newDate)}
          fullWidth
          margin="dense"
          required={!dateOfBirthUnknown}
          InputProps={{
            endAdornment: (
              <FormControlLabel
                control={
                  <Switch
                    checked={dateOfBirthUnknown}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setDateOfBirth(null)
                      }
                      setDateOfBirthUnknown(e.target.checked)
                    }}
                  />
                }
                label="Unknown"
              />
            ),
          }}
        />

        <Divider sx={{ marginY: 2 }} />
        <TextField
          label="Your Name"
          name="accessedBy"
          value={accessedBy}
          onChange={(e) => setAccessedBy(e.target.value)}
          fullWidth
          margin="dense"
          required
          helperText="Name of the person accessing the record"
        />
        <TextField
          label="Your Organisation"
          name="accessedByOrg"
          value={accessedByOrg}
          onChange={(e) => setAccessedByOrg(e.target.value)}
          fullWidth
          margin="dense"
          helperText="i.e. Summerville NHS Trust"
        />
        <FormControlLabel
          label="I confirm that I am authorised to access this patient care record and understand that unauthorised access or misuse of this information is a violation of data protection laws. I agree to the terms of use and acknowledge my responsibility to handle this data securely and in accordance with all applicable regulations."
          control={<Checkbox name="agreedTerms" required color="success" />}
          sx={{ marginY: 2 }}
          required
        />
        <Stack gap={2}>
          {!epcrData ? (
            <Button variant="contained" color="primary" fullWidth type="submit">
              Access Record
            </Button>
          ) : (
            <PDFDownloadLink
              document={
                <EpcrPDF
                  epcr={epcrData}
                  orgSettings={orgSettings}
                  onRender={() => {
                    API.post('/epcrreport/accesslog', {
                      epcrID: epcrData._id,
                      note: `Record Accessed using Code ${accessCode}`,
                      accessedBy: `${accessedBy} (${accessedByOrg})`,
                    })
                  }}
                  printedBy={`${accessedBy} (${accessedByOrg})`}
                />
              }
              fileName={() =>
                `${
                  epcrData.demographics.lastNameUnknown
                    ? 'UNKNOWN'
                    : epcrData.demographics.lastName
                }-${
                  epcrData.demographics.dobUnknown
                    ? 'UNKNOWN'
                    : moment(epcrData.demographics.dob).format('YYYY-MM-DD')
                }-${moment(epcrData.incident.incidentDate).format(
                  'YYYY-MM-DD'
                )}`
              }
            >
              {({ blob, url, loading, error }) => (
                <Button
                  variant="contained"
                  disabled={loading}
                  size="small"
                  fullWidth
                  color={loading ? 'warning' : 'success'}
                  onClick={() => {}}
                >
                  {loading ? 'Loading document...' : 'Download!'}
                </Button>
              )}
            </PDFDownloadLink>
          )}
          {failedRecaptcha && (
            <Alert severity="error" variant="filled">
              <AlertTitle color='white'>Failed Security Checks</AlertTitle>
              Sorry, your browser has failed security checks. Please ensure you
              are not viewing this page in a private browsing session.
            </Alert>
          )}
          {isDraft && (
            <Alert severity="error" variant="filled">
              <AlertTitle>Record Incomplete</AlertTitle>
              This record is still being completed. Please try again later.
            </Alert>
          )}
          {invalidError && (
            <Alert severity="error" variant="filled">
              <AlertTitle>No Matching Record Found</AlertTitle>Please ensure the
              Check Code is valid and that all patient details match.
            </Alert>
          )}
        </Stack>
      </Stack>
      {/* <BigModal open={!!epcrData} style={{ height: 600 }}>
        <Box minHeight="80vh" flexGrow={1} flex={1} height="100%">
          <PDFViewer width="100%" height="100%">
            <EpcrPDF
              epcr={epcrData}
              orgSettings={orgSettings}
              onRender={() => {
                API.post('/epcrreport/accesslog', {
                  epcrID: epcrData._id,
                  note: `Record Accessed using Code ${accessCode}`,
                  accessedBy: `${accessedBy} (${accessedByOrg})`,
                })
              }}
              printedBy={`${accessedBy} (${accessedByOrg})`}
            />
          </PDFViewer>
        </Box>
      </BigModal> */}
    </Grid>
  )
}

export default AccessForm
